/**
 * OptiHub › Account Audit Log — a customer's OWN activity log (the same rows their portal's
 * Audit Log page shows), looked up by Stripe ID or account ID. ROOT + SUPPORT.
 *
 * Read-only by design (requester, 2026-10-01): staff read the log and relay what they find to
 * the customer, so there is no export or download. Shows the customer's activity AND what staff
 * did inside the account through "Login as Customer" (User Type "Super User"; requester,
 * 2026-10-04) — those rows are staff-only and never reach the customer's own Audit Log. What
 * staff did FROM staff-app stays in Administration › Activity Log.
 *
 * Filters and paging are server-side across the account's whole retained history (2 years /
 * 250k rows per account — optisigns-job-logs retention), and every date is US Central.
 */
import { useEffect, useMemo, useState } from 'react';
import { RotateCcw, Search } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { FacetCombobox } from '@/features/mdm-devices/components/FacetCombobox';
import { CardShell } from '@/features/optihub/components/console-ui';
import { ACTION_FILTER_OPTIONS } from '../lib/audit-constants';
import { htmlToText } from '../lib/html-text';
import {
  useAccountAuditLogs,
  useAccountAuditResolve,
  type AccountAuditEntry,
  type AccountAuditSource,
  type AuditAccount,
} from '../hooks/useAccountAuditLog';
import { AuditActionBadge } from './AuditActionBadge';
import { UserTypeBadge } from './UserTypeBadge';

export const PAGE_SIZES = [20, 50, 100] as const;
const DESCRIPTION_DEBOUNCE_MS = 300;
const COLS = 7;

const ACTION_OPTIONS = ACTION_FILTER_OPTIONS.map((o) => ({ value: o.label, label: o.label }));

const SOURCE_OPTIONS: { value: AccountAuditSource; label: string }[] = [
  { value: 'ALL', label: 'All user types' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'SUPER_USER', label: 'Super User' },
];

/** One labelled identifier in the "right customer?" header; `—` when the account has none. */
function HeaderField({
  label,
  value,
  mono,
  testId,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  testId: string;
}) {
  return (
    <span className="text-muted-foreground">
      {`${label}: `}
      <span className={mono ? 'font-mono text-xs text-foreground' : 'text-foreground'} data-testid={testId}>
        {value || '—'}
      </span>
    </span>
  );
}

/**
 * Who did it: the EMAIL only (requester, 2026-10-07) — the staff member's `staffEmail` on a Super
 * User row, the customer user's email (resolved from `userId`) otherwise; the bare `userId` when
 * neither resolves. No name: job-logs no longer stores one (review M3 on job-logs !22), and User
 * Type already says whether it was a customer or a Super User.
 */
function AuditUserCell({ entry }: { entry: AccountAuditEntry }) {
  const email = entry.userEmail || entry.userId || '';
  return (
    <div className="min-w-0 truncate text-sm" title={email}>
      {email}
    </div>
  );
}

export function AccountAuditLogConsole() {
  const [lookup, setLookup] = useState('');
  const [account, setAccount] = useState<AuditAccount | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [resolve, { loading: resolving }] = useAccountAuditResolve();

  // Filters. Empty dates mean "the server's default" (last 30 days); the inputs then show the
  // range the server reports it applied, so nothing here reads the browser clock.
  const [source, setSource] = useState<AccountAuditSource>('ALL');
  const [actionLabels, setActionLabels] = useState<string[]>([]);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [description, setDescription] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const next = descriptionDraft.trim();
    // Only a CHANGED search resets the page — otherwise the mount-time pass (and every
    // whitespace-only keystroke) would throw the operator back to page 1.
    if (next === description) return;
    const t = setTimeout(() => {
      setDescription(next);
      setPage(0);
    }, DESCRIPTION_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [descriptionDraft, description]);

  const actionCodes = useMemo(
    () =>
      ACTION_FILTER_OPTIONS.filter((o) => actionLabels.includes(o.label)).flatMap((o) => o.codes),
    [actionLabels],
  );

  const vars = account
    ? {
        accountId: account.accountId,
        filter: {
          source: source === 'ALL' ? undefined : source,
          actions: actionCodes.length ? actionCodes : undefined,
          description: description || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        },
        pagination: { offset: page * pageSize, limit: pageSize },
      }
    : null;
  const { data, loading, error, refetch } = useAccountAuditLogs(vars);
  const result = data?.staffAccountAuditLogs;
  const entries = result?.items ?? [];
  const total = result?.totalCount ?? 0;
  // The server stops counting at 10,000. Past that the true page count is unknown, so Next stays
  // available while a page comes back full, and the labels say "10,000+".
  const capped = !!result?.totalCapped;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasNext = capped ? entries.length === pageSize : page + 1 < totalPages;
  const firstRow = page * pageSize + 1;
  const lastRow = page * pageSize + entries.length;
  const totalLabel = `${total.toLocaleString('en-US')}${capped ? '+' : ''}`;
  const rangeLabel =
    entries.length === 0
      ? 'No entries'
      : capped && lastRow > total
        ? `${firstRow.toLocaleString('en-US')}–${lastRow.toLocaleString('en-US')}`
        : `${firstRow.toLocaleString('en-US')}–${lastRow.toLocaleString('en-US')} of ${totalLabel}`;

  const resetFilters = () => {
    setSource('ALL');
    setActionLabels([]);
    setDescriptionDraft('');
    setDescription('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const runLookup = async () => {
    const q = lookup.trim();
    if (!q) return;
    setLookupError(null);
    try {
      const r = await resolve({ variables: { query: q } });
      // useLazyQuery resolves with the error for a GraphQL error and rejects for some
      // transport failures — both paths land here.
      if (r.error || !r.data) {
        setAccount(null);
        setLookupError(r.error?.message ?? 'No customer found.');
        return;
      }
      setAccount(r.data.staffAccountAuditResolve);
      resetFilters();
    } catch (e) {
      setAccount(null);
      setLookupError((e as Error)?.message ?? 'Could not look up the customer.');
    }
  };

  const filtered =
    source !== 'ALL' || actionLabels.length > 0 || !!description || !!dateFrom || !!dateTo;

  return (
    <CardShell
      testId="account-audit-log"
      title="Account Audit Log"
      description="Look up a customer by Stripe ID (cus_…) or account ID to see their account activity. Read-only."
    >
      <div className="flex flex-col gap-4">
        <div className="flex max-w-xl items-center gap-2">
          <Input
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void runLookup();
            }}
            placeholder="Stripe ID (cus_…) or account ID"
            aria-label="Stripe ID or account ID"
            data-testid="account-audit-lookup"
          />
          <Button
            onClick={() => void runLookup()}
            disabled={!lookup.trim() || resolving}
            className="cursor-pointer"
            data-testid="account-audit-lookup-button"
          >
            <Search className="mr-2 h-4 w-4" />
            {resolving ? 'Searching…' : 'Search'}
          </Button>
        </div>

        {lookupError ? (
          <p className="text-sm text-destructive" data-testid="account-audit-lookup-error">
            {lookupError}
          </p>
        ) : null}

        {account ? (
          <>
            <div
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-md border bg-muted/30 px-3 py-2 text-sm"
              data-testid="account-audit-header"
            >
              <HeaderField label="Owner email" value={account.ownerEmail} testId="account-audit-owner-email" />
              <HeaderField label="Account ID" value={account.accountId} mono testId="account-audit-account-id" />
              <HeaderField label="Stripe ID" value={account.stripeId} mono testId="account-audit-stripe-id" />
              {account.matchedTeam ? (
                <span className="text-xs text-muted-foreground" data-testid="account-audit-matched-team">
                  {`That Stripe ID is the team "${account.matchedTeam}" — showing its parent account.`}
                </span>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-3" data-testid="account-audit-filters">
              <Select
                value={source}
                onValueChange={(v) => {
                  setSource(v as AccountAuditSource);
                  setPage(0);
                }}
              >
                <SelectTrigger
                  className="w-[170px]"
                  aria-label="User Type"
                  data-testid="account-audit-source-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="w-[240px]">
                <FacetCombobox
                  label="Action"
                  allLabel="All actions"
                  pluralLabel="actions"
                  options={ACTION_OPTIONS}
                  value={actionLabels}
                  onChange={(v) => {
                    setActionLabels(v.filter((x): x is string => x !== null));
                    setPage(0);
                  }}
                  testId="account-audit-action-filter"
                />
              </div>
              <Input
                className="w-[260px]"
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                placeholder="Search description…"
                aria-label="Search description"
                maxLength={200}
                data-testid="account-audit-description"
              />
              <Input
                type="date"
                className="w-[9.5rem]"
                value={dateFrom || result?.dateFrom || ''}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(0);
                }}
                aria-label="From (CST)"
                data-testid="account-audit-date-from"
              />
              <span className="text-xs text-muted-foreground">{'to'}</span>
              <Input
                type="date"
                className="w-[9.5rem]"
                value={dateTo || result?.dateTo || ''}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(0);
                }}
                aria-label="To (CST)"
                data-testid="account-audit-date-to"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                disabled={!filtered}
                className="cursor-pointer"
                data-testid="account-audit-reset"
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                {'Reset'}
              </Button>
            </div>

            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[190px]">{'Log Time (CST)'}</TableHead>
                    <TableHead className="w-[200px]">{'User'}</TableHead>
                    <TableHead className="w-[120px]">{'User Type'}</TableHead>
                    <TableHead className="w-[150px]">{'Action'}</TableHead>
                    <TableHead>{'Description'}</TableHead>
                    <TableHead className="w-[140px]">{'Team'}</TableHead>
                    <TableHead className="w-[130px]">{'IP Address'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {error ? (
                    <TableRow>
                      <TableCell colSpan={COLS} className="py-12 text-center text-sm">
                        <p className="text-destructive" data-testid="account-audit-error">
                          {error.message}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 cursor-pointer"
                          onClick={() => void refetch()}
                        >
                          {'Retry'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : loading && entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLS} className="py-12 text-center text-sm text-muted-foreground">
                        {'Loading activity…'}
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={COLS}
                        className="py-12 text-center text-sm text-muted-foreground"
                        data-testid="account-audit-empty"
                      >
                        {filtered
                          ? 'No activity matches these filters.'
                          : 'No activity in this range. Logs are kept for up to 2 years.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((e) => {
                      // Newer rows carry the sentence without its actor; older ones only `text`.
                      const text = htmlToText(e.description || e.text);
                      return (
                        <TableRow key={e.id} data-testid={`account-audit-row-${e.id}`}>
                          <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                            {e.time}
                          </TableCell>
                          <TableCell className="max-w-[200px]" data-testid={`account-audit-user-${e.id}`}>
                            <AuditUserCell entry={e} />
                          </TableCell>
                          <TableCell data-testid={`account-audit-user-type-${e.id}`}>
                            <UserTypeBadge isSuperUser={e.isSuperUser} />
                          </TableCell>
                          <TableCell>
                            <AuditActionBadge action={e.action} />
                          </TableCell>
                          {/* Wraps rather than truncates: staff read this to answer the customer. */}
                          <TableCell className="min-w-[280px] whitespace-normal break-words text-sm">
                            {text}
                          </TableCell>
                          <TableCell className="text-sm">{e.teamName || ''}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {e.ipAddress || ''}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3" data-testid="account-audit-pagination">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{'Rows per page'}</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="h-8 w-[76px]" data-testid="account-audit-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {String(n)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span data-testid="account-audit-range">
                  {rangeLabel}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  data-testid="account-audit-prev"
                >
                  {'← Prev'}
                </Button>
                <span className="text-xs text-muted-foreground">{capped ? `Page ${page + 1}` : `Page ${page + 1} / ${totalPages}`}</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() => setPage((p) => (hasNext ? p + 1 : p))}
                  disabled={!hasNext || loading}
                  data-testid="account-audit-next"
                >
                  {'Next →'}
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </CardShell>
  );
}
